// One selected object's current access (`GET /api/access`): who holds what,
// whether it is stored on this object (direct, revocable here) or inherited
// (indirect, read-only with its via-path — revoke it where it lives instead).
// Collapsed by default, with the direct/inherited counts in the title, so many
// selected objects stay scannable; revoke selection lives in the page, so
// collapsed rows keep their staged checkboxes.
import {
  faCircleExclamation,
  faRobot,
  faUser,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Checkbox,
  Grid,
  Icon,
  Table,
  Tooltip,
} from "@intility/bifrost-react";
import Accordion from "@intility/bifrost-react/Accordion";
import { FgaErrorCodes, IntilityIdException } from "~/api/errorEnvelope";
import { type AccessStoreRole, useObjectAccess } from "~/api/grants";
import type { CatalogCompany } from "~/api/types";
import { relationBadgeState } from "~/lib/relationBadges";
import {
  type AccessRow,
  flattenAccessRows,
  objectLabel,
  ROLE_LABEL,
  subjectLabel,
} from "./labels";

export interface RevokeSelectionItem {
  objectId: string;
  subject: string;
  relation: AccessStoreRole;
  label: string;
}

export function rowKey(
  objectId: string,
  subject: string,
  relation: string,
): string {
  return `${objectId}|${subject}|${relation}`;
}

const SUBJECT_TYPE_LABEL: Record<string, string> = {
  user: "User",
  client: "Client",
  group: "Group",
};

function SubjectTypeIcon({ type }: { type: string }) {
  if (type === "group") return <Icon icon={faUsers} />;
  if (type === "client") return <Icon icon={faRobot} />;
  return <Icon icon={faUser} />;
}

export function ObjectAccessTable({
  objectId,
  companies,
  selected,
  onToggle,
}: {
  objectId: string;
  companies: CatalogCompany[] | undefined;
  selected: Set<string>;
  onToggle: (key: string, item: RevokeSelectionItem | null) => void;
}) {
  const { data, isLoading, error } = useObjectAccess(objectId);

  if (isLoading) {
    return (
      <Grid
        gap={4}
        style={{
          gridAutoFlow: "column",
          justifyContent: "start",
          placeItems: "center",
        }}
      >
        <span className="bf-h4" style={{ margin: 0 }}>
          <Tooltip content={objectId}>
            <span>{objectLabel(objectId, companies)}</span>
          </Tooltip>
        </span>
        <Icon.Spinner size={20} />
      </Grid>
    );
  }

  if (error) {
    if (
      error instanceof IntilityIdException &&
      error.code === FgaErrorCodes.Forbidden
    ) {
      return (
        <MessageWithLabel
          objectId={objectId}
          companies={companies}
          text="You do not hold admin on this object (anymore), so its access cannot be shown."
        />
      );
    }
    return (
      <MessageWithLabel
        objectId={objectId}
        companies={companies}
        text={error instanceof Error ? error.message : "Failed to load access."}
      />
    );
  }

  const { directRows, indirectRows } = flattenAccessRows(data);

  if (directRows.length === 0 && indirectRows.length === 0) {
    return (
      <MessageWithLabel
        objectId={objectId}
        companies={companies}
        text="No one has access through this object yet."
      />
    );
  }

  function ViaCell({ row }: { row: AccessRow }) {
    if (row.access === "direct") {
      return <span className="bf-small bfc-base-2">Direct grant</span>;
    }
    return (
      <Tooltip content={row.via ?? "Inherited"}>
        <span className="bf-small bfc-base-2">
          {row.via === "inherited" ? "Inherited from above" : row.via}
        </span>
      </Tooltip>
    );
  }

  function SubjectTypeCell({ type }: { type: string }) {
    return (
      <Grid
        gap={4}
        style={{
          gridAutoFlow: "column",
          justifyContent: "start",
          placeItems: "center",
        }}
      >
        <SubjectTypeIcon type={type} />
        {SUBJECT_TYPE_LABEL[type] ?? type}
      </Grid>
    );
  }

  const rows: { row: AccessRow; revocable: boolean }[] = [
    ...directRows.map((row) => ({ row, revocable: true })),
    ...indirectRows.map((row) => ({ row, revocable: false })),
  ];

  return (
    <Accordion variant="styled">
      <Accordion.Item
        title={
          <Grid gap={2}>
            <span>{objectLabel(objectId, companies)}</span>
            <span className="bf-small bfc-base-2">
              {directRows.length} direct
              {indirectRows.length > 0 && `, ${indirectRows.length} inherited`}
            </span>
          </Grid>
        }
        htmlTitle={objectId}
        noPadding
      >
        <Grid gap={8} style={{ padding: "12px 16px 16px" }}>
          <Table noBorder>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  aria-label="Select for revoke"
                  style={{ width: 40 }}
                />
                <Table.HeaderCell>Subject</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Via</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map(({ row, revocable }) => {
                const key = rowKey(objectId, row.subject, row.relation);
                const label = subjectLabel(row.subject, row.info);
                return (
                  <Table.Row
                    key={key}
                    style={revocable ? undefined : { opacity: 0.7 }}
                  >
                    <Table.Cell>
                      {revocable && (
                        <Checkbox
                          label={`Revoke ${label}'s ${row.relation} on ${objectId}`}
                          hideLabel
                          checked={selected.has(key)}
                          onChange={() =>
                            onToggle(
                              key,
                              selected.has(key)
                                ? null
                                : {
                                    objectId,
                                    subject: row.subject,
                                    relation: row.relation,
                                    label,
                                  },
                            )
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {label}
                      {row.info?.email && (
                        <span
                          className="bf-small bfc-base-2"
                          style={{ display: "block" }}
                        >
                          {row.info.email}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <SubjectTypeCell type={row.subjectType} />
                    </Table.Cell>
                    <Table.Cell>
                      <Badge state={relationBadgeState(row.relation, "access")}>
                        {ROLE_LABEL[row.relation] ?? row.relation}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <ViaCell row={row} />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          {indirectRows.length > 0 && (
            <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
              <Icon icon={faCircleExclamation} marginRight />
              Inherited rows are read-only: the grant lives on{" "}
              {indirectRows.some((r) => r.via?.startsWith("group:"))
                ? "a group or "
                : ""}
              an object above this one. Revoke it there instead.
            </p>
          )}
        </Grid>
      </Accordion.Item>
    </Accordion>
  );
}

function MessageWithLabel({
  objectId,
  companies,
  text,
}: {
  objectId: string;
  companies: CatalogCompany[] | undefined;
  text: string;
}) {
  return (
    <Grid gap={4}>
      <span className="bf-h4" style={{ margin: 0 }}>
        <Tooltip content={objectId}>
          <span>{objectLabel(objectId, companies)}</span>
        </Tooltip>
      </span>
      <p className="bf-small bfc-base-2" style={{ margin: 0 }}>
        {text}
      </p>
    </Grid>
  );
}
